/**
 * RecipeCardModal — pops open a printable recipe card for one meal from
 * the AI-generated household plan. Includes:
 *   • Per-serving Nutrition Facts (always emits all fields)
 *   • Dietary tag chips
 *   • Machine-scale picker
 *   • Unit family toggle (Metric / US) + per-ingredient unit override
 *   • Print / PDF (both drive the SHARED branded template)
 *   • Share + Save-to-Recipe-Center
 *
 * Data-testids:
 *   - lm-recipe-card, lm-recipe-card-close, lm-recipe-card-print,
 *     lm-recipe-card-pdf, lm-recipe-card-share, lm-recipe-card-save,
 *     lm-recipe-card-unit-family, lm-recipe-card-unit-row-{i}
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  X, ChefHat, Printer, FileText, Share2, Bookmark, BookmarkCheck,
  Clock, Snowflake, Droplets, Package, Scale, Loader2, Ruler,
} from 'lucide-react';
import AIProgressIndicator from './AIProgressIndicator';
import { buildBrandedHtml, addCanvasAsPages, TAG_LABEL } from '../../utils/recipeCardTemplate';
import {
  UNIT_FAMILIES, ALL_KITCHEN_UNITS, UNIT_LABELS,
  unitDimension, convertQty, autoUnit,
} from '../../utils/unitConvert';

const MACHINE_PRESETS = [
  { key: 'original', label: 'Original recipe',   capacityKg: 0    },
  { key: 'small',    label: 'Small (2 kg / ~4 tray)',  capacityKg: 2  },
  { key: 'medium',   label: 'Medium (4 kg / ~5 tray)', capacityKg: 4  },
  { key: 'large',    label: 'Large (7 kg / ~6 tray)',  capacityKg: 7  },
  { key: 'pro',      label: 'Pro (10 kg / commercial)',capacityKg: 10 },
];

export default function RecipeCardModal({ meal, open, onClose }) {
  const { API } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [card, setCard]       = useState(null);
  const [saved, setSaved]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [scaleKey, setScaleKey] = useState('original');
  const [unitFamily, setUnitFamily] = useState('as-written');
  const [rowUnitOverrides, setRowUnitOverrides] = useState({}); // {i: unit}

  // Load the operator's saved default unit family so every recipe card
  // opens in the units they actually use. Silent on failure — the modal
  // still works with the 'as-written' default. Only runs once per session
  // because the value is unlikely to change mid-cook.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/user/recipe-preferences`)
      .then((r) => { if (!cancelled) setUnitFamily(r.data?.unit_family || 'as-written'); })
      .catch(() => { /* silent — anon or first-time user */ });
    return () => { cancelled = true; };
  }, [API]);

  // Persist the operator's choice back to their profile so the next card
  // they open lands on the same family. Skip the initial render + the
  // reset-on-open effect so we don't overwrite the saved value with the
  // temporary 'as-written' state that fires while a new card is loading.
  const persistUnitFamily = (next) => {
    setUnitFamily(next);
    setRowUnitOverrides({});
    axios.put(`${API}/user/recipe-preferences`, { unit_family: next })
      .catch(() => { /* silent — best-effort */ });
  };

  useEffect(() => {
    if (!open || !meal) return;
    setCard(null);
    setSaved(false);
    setScaleKey('original');
    setRowUnitOverrides({});
    setImageUrl(meal?.image_data_url || null);
    setLoading(true);
    axios
      .post(`${API}/tools/recipe-card-ai`, {
        meal_name: meal.name,
        servings:  meal.servings || 4,
      })
      .then((r) => {
        setCard(r.data);
        if (!meal?.image_data_url && r.data?.recipe_id) {
          setImgLoading(true);
          axios
            .post(`${API}/tools/recipe-center/${r.data.recipe_id}/generate-image`)
            .then((imgR) => setImageUrl(imgR.data.image_data_url))
            .catch(() => { /* silent */ })
            .finally(() => setImgLoading(false));
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Recipe card failed'))
      .finally(() => setLoading(false));
  }, [open, meal, API]);

  // Machine-scaled variant of the AI recipe. Keeps `card` as canonical
  // so switching back to Original doesn't compound rounding errors.
  const scaled = useMemo(() => {
    if (!card) return null;
    const preset = MACHINE_PRESETS.find((m) => m.key === scaleKey) || MACHINE_PRESETS[0];
    if (!preset.capacityKg) return { ...card, __scale: null };

    const perServingKg = Number(card.total_wet_kg_per_serving) || 0.4;
    const targetServings = Math.max(1, Math.round(preset.capacityKg / perServingKg));
    const ratio = targetServings / Math.max(1, card.servings || 1);

    return {
      ...card,
      servings: targetServings,
      ingredients: (card.ingredients || []).map((it) => ({
        ...it,
        quantity: Number.isFinite(it.quantity)
          ? Number((it.quantity * ratio).toFixed(2))
          : it.quantity,
      })),
      __scale: {
        machine: preset.label,
        capacityKg: preset.capacityKg,
        perServingKg,
        targetServings,
        ratio,
        totalBatchKg: (targetServings * perServingKg).toFixed(2),
      },
    };
  }, [card, scaleKey]);

  // Apply unit conversions on top of the scaled ingredients. Per-row
  // override wins; otherwise the card-level family picks a sensible
  // target unit. Unknown units (e.g. "whole", "clove") pass through.
  const displayIngredients = useMemo(() => {
    if (!scaled?.ingredients) return [];
    return scaled.ingredients.map((it, i) => {
      const override = rowUnitOverrides[i];
      if (override) {
        return { ...it, ...convertQty(it.quantity, it.unit, override) };
      }
      if (unitFamily !== 'as-written') {
        return { ...it, ...autoUnit(it.quantity, it.unit, unitFamily) };
      }
      return it;
    });
  }, [scaled, rowUnitOverrides, unitFamily]);

  if (!open || !meal) return null;

  const safeName = () =>
    ((scaled?.name || card?.name) || 'recipe')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60);

  // The `view` used for Print + PDF — combines machine scaling AND unit
  // conversion so the branded output matches what the user sees on screen.
  const buildPrintView = () => {
    if (!scaled) return null;
    return { ...scaled, ingredients: displayIngredients };
  };

  const scaleLine = scaled?.__scale
    ? ` · Scaled for ${scaled.__scale.machine} (${scaled.__scale.totalBatchKg} kg wet)`
    : '';

  const printCard = () => {
    if (!card) return;
    const view = buildPrintView();
    const win = window.open('', '_blank', 'width=800,height=1000');
    if (!win) {
      toast.error('Popup blocked — allow popups for this site to print.');
      return;
    }
    win.document.write(buildBrandedHtml(
      [{ view, imageUrl, scaleLine }],
      { withPrintScript: true },
    ));
    win.document.close();
  };

  const exportPdf = async () => {
    if (!card) return;
    const view = buildPrintView();
    // Render the SAME branded HTML the Print sheet uses into a hidden
    // off-screen iframe sized like a letter page, then rasterise the
    // iframe body with html2canvas. PDF matches print pixel-for-pixel.
    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, {
      position: 'fixed', left: '-9999px', top: '0',
      width: '816px', height: '1056px', border: '0', background: '#fff',
    });
    document.body.appendChild(iframe);
    try {
      const doc = iframe.contentDocument;
      doc.open();
      doc.write(buildBrandedHtml(
        [{ view, imageUrl, scaleLine }],
        { withPrintScript: false },
      ));
      doc.close();
      await new Promise((r) => setTimeout(r, 150));
      const img = doc.querySelector('img.photo');
      if (img && !img.complete) {
        await new Promise((r) => {
          img.addEventListener('load',  r, { once: true });
          img.addEventListener('error', r, { once: true });
          setTimeout(r, 4000);
        });
      }
      const canvas = await html2canvas(doc.body, {
        background: '#ffffff', scale: 2, useCORS: true,
        width: 816, height: doc.body.scrollHeight,
        windowWidth: 816, windowHeight: doc.body.scrollHeight,
      });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      // Spill overflow onto extra letter pages — previously we forced the
      // whole recipe into ONE image at (0,0) with a height that could
      // exceed 279 mm, so the PDF viewer clipped everything past the
      // page edge (nutrition, footer, etc.). addCanvasAsPages() paginates.
      addCanvasAsPages(pdf, canvas);
      pdf.save(`${safeName()}.pdf`);
      toast.success('Recipe PDF downloaded.');
    } catch (e) {
      toast.error(`PDF export failed: ${e.message || e}`);
    } finally {
      iframe.remove();
    }
  };

  const shareCard = async () => {
    if (!card) return;
    const view = buildPrintView();
    const summary =
      `${view.name} — ${view.servings} servings\n\n` +
      `Ingredients:\n${(view.ingredients || [])
        .map((it) => `• ${it.quantity} ${it.unit} ${it.item}`).join('\n')}\n\n` +
      `Instructions:\n${(view.instructions || [])
        .map((s, i) => `${i + 1}. ${s}`).join('\n')}\n\n` +
      (view.freeze_dry_prep ? `Freeze-dry prep: ${view.freeze_dry_prep}\n` : '') +
      (view.rehydration     ? `Rehydration: ${view.rehydration}\n`         : '') +
      (view.storage         ? `Storage: ${view.storage}\n`                 : '') +
      `\n— Freeze Dry Academy`;
    try {
      if (navigator.share) {
        await navigator.share({ title: view.name, text: summary });
        toast.success('Shared.');
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(summary);
        toast.success('Recipe copied to clipboard — paste anywhere to share.');
      } else {
        toast.warning('Share not supported on this browser — use PDF instead.');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error(`Share failed: ${e?.message || e}`);
    }
  };

  const saveToCenter = async () => {
    if (!card) return;
    setSaving(true);
    try {
      const { data: savedRow } = await axios.post(`${API}/tools/recipe-center`, {
        name:             card.name,
        servings:         card.servings,
        prep_time_min:    card.prep_time_min,
        cook_time_min:    card.cook_time_min,
        ingredients:      card.ingredients,
        instructions:     card.instructions,
        freeze_dry_prep:  card.freeze_dry_prep,
        rehydration:      card.rehydration,
        storage:          card.storage,
        nutrition:        card.nutrition,
        nutrition_note:   card.nutrition_note,
        dietary_tags:     card.dietary_tags,
        source_meal:      meal.name,
      });
      setSaved(true);
      toast.success('Saved to Recipe Center — generating photo…');
      axios
        .post(`${API}/tools/recipe-center/${savedRow.id}/generate-image`)
        .then(() => toast.success('AI photo ready in the Community Recipe Book.'))
        .catch(() => { /* silent */ });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const nutrition = scaled?.nutrition || {};
  const hasNutrition = Object.values(nutrition).some(
    (v) => v !== null && v !== undefined && v !== '',
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 no-print"
      onClick={onClose}
      data-testid="lm-recipe-card"
    >
      <Card
        className="max-w-2xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 sticky top-0 bg-card z-10 border-b border-border">
          <CardTitle className="text-base flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-primary" /> Recipe card
          </CardTitle>
          <div className="flex gap-1 items-center flex-wrap justify-end">
            <Button type="button" size="sm" variant="outline" onClick={printCard} disabled={!card} data-testid="lm-recipe-card-print">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={exportPdf} disabled={!card} data-testid="lm-recipe-card-pdf">
              <FileText className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={shareCard} disabled={!card} data-testid="lm-recipe-card-share">
              <Share2 className="w-3.5 h-3.5 mr-1" /> Share
            </Button>
            <Button
              type="button" size="sm"
              variant={saved ? 'default' : 'outline'}
              onClick={saveToCenter} disabled={!card || saving || saved}
              data-testid="lm-recipe-card-save"
            >
              {saved
                ? <><BookmarkCheck className="w-3.5 h-3.5 mr-1" /> Saved</>
                : <><Bookmark className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}</>}
            </Button>
            <button
              type="button" onClick={onClose}
              className="text-muted-foreground hover:text-foreground pl-1"
              aria-label="Close" data-testid="lm-recipe-card-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <AIProgressIndicator
            active={loading}
            estimatedMs={9000}
            data-testid="lm-recipe-card-progress"
            steps={[
              { at: 0,    label: 'Composing ingredient list…' },
              { at: 3000, label: 'Writing prep + freeze-dry steps…' },
              { at: 6500, label: 'Finalising nutrition + dietary tags…' },
            ]}
          />

          {card && (
            <>
              <div className="flex flex-wrap items-center gap-3 no-print">
                <div className="flex items-center gap-2" data-testid="lm-recipe-card-scale">
                  <Scale className="w-4 h-4 text-primary" />
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Machine
                  </span>
                  <select
                    value={scaleKey}
                    onChange={(e) => setScaleKey(e.target.value)}
                    className="h-8 px-2 rounded-md border border-input bg-background text-xs"
                    data-testid="lm-recipe-card-scale-select"
                  >
                    {MACHINE_PRESETS.map((m) => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Ruler className="w-4 h-4 text-primary" />
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Units
                  </span>
                  <select
                    value={unitFamily}
                    onChange={(e) => persistUnitFamily(e.target.value)}
                    className="h-8 px-2 rounded-md border border-input bg-background text-xs"
                    data-testid="lm-recipe-card-unit-family"
                  >
                    {UNIT_FAMILIES.map((f) => (
                      <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                  </select>
                </div>
                {scaled?.__scale && (
                  <span className="text-[11px] text-muted-foreground w-full">
                    Fills to <b>{scaled.__scale.totalBatchKg} kg wet</b> ·{' '}
                    <b>{scaled.__scale.targetServings}</b> servings ·{' '}
                    ×{scaled.__scale.ratio.toFixed(2)} of original
                  </span>
                )}
              </div>

              <div
                className="bg-white text-black rounded-md p-6 space-y-3"
                style={{ fontFamily: '"Helvetica Neue", Arial, sans-serif' }}
                data-testid="lm-recipe-card-body"
              >
                <div className="flex items-baseline justify-between border-b border-black pb-2">
                  <div>
                    <div className="text-2xl font-extrabold leading-tight">{scaled.name}</div>
                    <div className="text-xs uppercase tracking-wider text-slate-600">
                      Yields {scaled.servings} servings
                      {scaled.__scale && (
                        <span className="text-primary ml-2 normal-case">
                          · Scaled for {scaled.__scale.machine}
                        </span>
                      )}
                    </div>
                  </div>
                  {(scaled.prep_time_min || scaled.cook_time_min) && (
                    <div className="text-right text-xs">
                      {scaled.prep_time_min ? (
                        <div><Clock className="inline w-3 h-3 mr-1" />Prep {scaled.prep_time_min} min</div>
                      ) : null}
                      {scaled.cook_time_min ? (
                        <div><Clock className="inline w-3 h-3 mr-1" />Cook {scaled.cook_time_min} min</div>
                      ) : null}
                    </div>
                  )}
                </div>

                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={scaled.name}
                    className="w-full max-h-64 object-cover rounded-md border border-slate-300"
                    data-testid="lm-recipe-card-image"
                  />
                ) : imgLoading ? (
                  <div className="w-full h-40 flex items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-slate-500 text-sm">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Painting recipe photo…
                  </div>
                ) : null}

                {scaled.dietary_tags?.length > 0 && (
                  <div className="flex gap-1 flex-wrap" data-testid="lm-recipe-card-tags">
                    {scaled.dietary_tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-600 text-emerald-800 bg-emerald-50"
                      >
                        {TAG_LABEL[t] || t}
                      </span>
                    ))}
                  </div>
                )}

                {displayIngredients.length > 0 && (
                  <div>
                    <div className="text-xs uppercase font-bold tracking-wider text-slate-700 mb-1">
                      Ingredients
                    </div>
                    <ul className="text-sm space-y-1">
                      {displayIngredients.map((it, i) => {
                        // Show ALL kitchen units in every row so an
                        // operator can swap grams → teaspoons, cups → oz,
                        // etc. Non-convertible source units (e.g. "whole",
                        // "clove") show a static label instead.
                        const swappable = unitDimension(it.unit) !== null;
                        return (
                          <li key={i} className="flex gap-2 items-center">
                            <span className="font-mono w-16 shrink-0 text-right">
                              {Number.isFinite(it.quantity) ? it.quantity : ''}
                            </span>
                            {swappable ? (
                              <select
                                value={it.unit || ''}
                                onChange={(e) => setRowUnitOverrides((prev) => ({
                                  ...prev,
                                  [i]: e.target.value,
                                }))}
                                className="h-6 px-1 rounded border border-slate-300 bg-white text-[11px] font-mono"
                                data-testid={`lm-recipe-card-unit-row-${i}`}
                              >
                                {ALL_KITCHEN_UNITS.map((u) => (
                                  <option key={u} value={u}>{UNIT_LABELS[u] || u}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="font-mono w-14 shrink-0 text-[11px]">{it.unit}</span>
                            )}
                            <span className="flex-1">{it.item}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {scaled.instructions?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase font-bold tracking-wider text-slate-700 mb-1">
                      Instructions
                    </div>
                    <ol className="text-sm list-decimal ml-5 space-y-1">
                      {scaled.instructions.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {scaled.freeze_dry_prep && <InfoRow icon={Snowflake} label="Freeze-dry prep" value={scaled.freeze_dry_prep} />}
                {scaled.rehydration     && <InfoRow icon={Droplets}  label="Rehydration"      value={scaled.rehydration} />}
                {scaled.storage         && <InfoRow icon={Package}   label="Storage"          value={scaled.storage} />}

                {hasNutrition && (
                  <div
                    className="border-2 border-black rounded-md p-3 mt-3"
                    data-testid="lm-recipe-card-nutrition"
                  >
                    <div className="text-lg font-extrabold leading-tight border-b-4 border-black pb-1">
                      Nutrition Facts (per serving)
                    </div>
                    {nutrition.serving_size && (
                      <div className="text-[11px] mb-1">Serving: {nutrition.serving_size}</div>
                    )}
                    <div className="flex items-baseline justify-between border-b-2 border-black py-1.5">
                      <span className="font-bold text-sm">Calories</span>
                      <span className="text-lg font-extrabold">
                        {nutrition.calories ?? '—'}
                      </span>
                    </div>
                    <NutriRow label="Total Fat"      value={nutrition.total_fat_g}     unit="g" />
                    <NutriRow label="Saturated Fat"  value={nutrition.saturated_fat_g} unit="g" indent />
                    <NutriRow label="Trans Fat"      value={nutrition.trans_fat_g}     unit="g" indent />
                    <NutriRow label="Cholesterol"    value={nutrition.cholesterol_mg}  unit="mg" />
                    <NutriRow label="Sodium"         value={nutrition.sodium_mg}       unit="mg" />
                    <NutriRow label="Total Carbs"    value={nutrition.total_carbs_g}   unit="g" />
                    <NutriRow label="Dietary Fiber"  value={nutrition.dietary_fiber_g} unit="g" indent />
                    <NutriRow label="Total Sugars"   value={nutrition.total_sugars_g}  unit="g" indent />
                    <NutriRow label="Added Sugars"   value={nutrition.added_sugars_g}  unit="g" indent />
                    <NutriRow label="Protein"        value={nutrition.protein_g}       unit="g" />
                    <div className="text-[9px] italic text-slate-600 mt-1.5 leading-snug">
                      Per-serving values — do NOT change with machine scaling.
                      AI-estimated; rows shown as "—" were not returned and can be filled in from a certified lab before commercial labelling.
                    </div>
                  </div>
                )}

                {scaled.nutrition_note && (
                  <div className="text-[11px] italic text-slate-600 border-t border-slate-200 pt-2 mt-2">
                    {scaled.nutrition_note}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="text-sm">
      <div className="text-xs uppercase font-bold tracking-wider text-slate-700 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div>{value}</div>
    </div>
  );
}

// Nutrition row — ALWAYS render. Missing values render as em-dash so the
// panel stays complete even when the AI didn't return every field.
function NutriRow({ label, value, unit, indent = false }) {
  const missing = value === null || value === undefined || value === '';
  return (
    <div className="flex justify-between text-[12px] border-b border-slate-300 py-1">
      <span style={{ paddingLeft: indent ? 16 : 0 }}>
        {indent ? label : <b>{label}</b>}
      </span>
      <span className="font-mono">
        {missing ? '—' : `${value}${unit || ''}`}
      </span>
    </div>
  );
}
